import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { AppContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const CheckoutForm = ({ draftId, total, currency, onSuccess, onCancel, language }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const labels = {
    en: { pay: 'Pay Now', processing: 'Processing...', cancel: 'Cancel', secure: 'Secure payment powered by Stripe' },
    tr: { pay: 'Şimdi Öde', processing: 'İşleniyor...', cancel: 'İptal', secure: 'Stripe ile güvenli ödeme' },
    el: { pay: 'Πληρωμή', processing: 'Επεξεργασία...', cancel: 'Ακύρωση', secure: 'Ασφαλής πληρωμή μέσω Stripe' },
    de: { pay: 'Jetzt Bezahlen', processing: 'Verarbeitung...', cancel: 'Abbrechen', secure: 'Sichere Zahlung über Stripe' },
  };
  const t = labels[language] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/success`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        const res = await axios.post(
          `${API}/checkout/confirm-payment-intent`,
          { paymentIntentId: paymentIntent.id },
          { withCredentials: true }
        );
        onSuccess(res.data.order_id);
      } catch (err) {
        setError(err.response?.data?.detail || 'Order confirmation failed');
      }
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: { applePay: 'auto', googlePay: 'auto' },
        }}
      />

      {error && (
        <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg">{error}</div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-[#C8A951] hover:bg-[#b89842] text-white py-3 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? t.processing : `${t.pay} — $${total?.toFixed(2)}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition"
        >
          {t.cancel}
        </button>
      </div>

      <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        {t.secure}
      </p>
    </form>
  );
};

const StripeInlineCheckout = ({ draftId, total, currency, onSuccess, onCancel, language }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!draftId || !stripePromise) return;
    setLoading(true);
    axios
      .post(`${API}/checkout/create-payment-intent`, { draft_id: draftId, currency: currency || 'usd' }, { withCredentials: true })
      .then((res) => {
        setClientSecret(res.data.clientSecret);
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'Failed to initialize payment');
      })
      .finally(() => setLoading(false));
  }, [draftId, currency]);

  if (!stripePromise) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-3 border-[#C8A951] border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Initializing payment...</span>
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 text-red-700 text-sm p-4 rounded-lg">{error}</div>;
  }

  if (!clientSecret) return null;

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#C8A951',
      colorBackground: '#ffffff',
      fontFamily: 'Inter, sans-serif',
      borderRadius: '8px',
    },
  };

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <CheckoutForm
        draftId={draftId}
        total={total}
        currency={currency}
        onSuccess={onSuccess}
        onCancel={onCancel}
        language={language}
      />
    </Elements>
  );
};

export const isStripeInlineAvailable = () => !!STRIPE_PK;

export default StripeInlineCheckout;
